import { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import SignatureCanvas from 'react-signature-canvas';

const SignaturePad = forwardRef(({ height = 200, className = '', 'data-testid': testId }, ref) => {
  const sigRef = useRef(null);
  const wrapperRef = useRef(null);

  useImperativeHandle(ref, () => ({
    isEmpty: () => sigRef.current?.isEmpty(),
    clear: () => sigRef.current?.clear(),
    toDataURL: (type) => sigRef.current?.toDataURL(type),
  }));

  const resizeCanvas = useCallback(() => {
    const canvas = sigRef.current?.getCanvas();
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const data = sigRef.current.toDataURL();
    const wasEmpty = sigRef.current.isEmpty();

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const w = wrapper.offsetWidth;
    const h = wrapper.offsetHeight;

    canvas.width = w * ratio;
    canvas.height = h * ratio;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);

    if (!wasEmpty) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, w, h);
      };
      img.src = data;
    }
  }, []);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  // Block page scroll while touching the signature canvas
  useEffect(() => {
    const canvas = sigRef.current?.getCanvas();
    if (!canvas) return;

    const preventScroll = (e) => {
      e.preventDefault();
    };

    canvas.addEventListener('touchstart', preventScroll, { passive: false });
    canvas.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', preventScroll);
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`relative ${className}`}
      style={{ height: `${height}px`, touchAction: 'none', overscrollBehavior: 'none' }}
      data-testid={testId}
    >
      <SignatureCanvas
        ref={sigRef}
        canvasProps={{
          style: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            msTouchAction: 'none',
          },
        }}
        minWidth={1.5}
        maxWidth={3}
        velocityFilterWeight={0.7}
      />
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';
export default SignaturePad;
